import React from 'react';
import { Box, Container, Grid, Typography, IconButton, Link, Divider, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import FavoriteIcon from '@mui/icons-material/Favorite';

const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: 'auto',
  position: 'relative',
  zIndex: 1,
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  fontSize: '1rem',
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  marginBottom: theme.spacing(0.75),
  display: 'block',
  transition: 'color 0.2s ease',
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
  },
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
  transition: 'all 0.2s ease',
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  '&:hover': {
    color: theme.palette.primary.main,
    transform: 'translateY(-3px)',
    backgroundColor: theme.palette.background.default,
  },
}));

const CopyrightText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.85rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About Me', href: '#about' },
    { name: 'Projects', href: '#projects' },
    { name: 'Skills', href: '#skills' },
    { name: 'Contact', href: '#contact' },
  ];

  const contactInfo = [
    { name: 'Email', value: 'ak21367185@gmail.com', href: 'mailto:ak21367185@gmail.com' },
    { name: 'LinkedIn', value: 'linkedin.com/in/kumarav1nash', href: 'https://linkedin.com/in/kumarav1nash' },
    { name: 'GitHub', value: 'github.com/kumarav1nash', href: 'https://github.com/kumarav1nash' },
  ];

  return (
    <FooterContainer component="footer">
      <Container maxWidth="lg">
        <Grid container spacing={4} justifyContent="space-between">
          <Grid item xs={12} sm={6} md={4}>
            <FooterTitle variant="h6">
              <span style={{ color: theme.palette.secondary.main }}>/</span>
              KumarAv1nash
            </FooterTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '300px' }}>
              Software Engineer focused on building scalable, high-performance backend systems and cloud solutions.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <SocialIcon aria-label="GitHub" component="a" href="https://github.com/kumarav1nash" target="_blank">
                <GitHubIcon fontSize="small" />
              </SocialIcon>
              <SocialIcon aria-label="LinkedIn" component="a" href="https://linkedin.com/in/kumarav1nash" target="_blank">
                <LinkedInIcon fontSize="small" />
              </SocialIcon>
              <SocialIcon aria-label="Email" component="a" href="mailto:ak21367185@gmail.com">
                <EmailIcon fontSize="small" />
              </SocialIcon>
            </Box>
          </Grid>
          
          <Grid item xs={6} sm={3} md={2}>
            <FooterTitle variant="h6">Quick Links</FooterTitle>
            {quickLinks.map((link) => (
              <FooterLink 
                key={link.name} 
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector(link.href).scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {link.name}
              </FooterLink>
            ))}
          </Grid>
          
          <Grid item xs={6} sm={3} md={3}>
            <FooterTitle variant="h6">Contact</FooterTitle>
            {contactInfo.map((info) => (
              <FooterLink 
                key={info.name} 
                href={info.href}
                target={info.name !== 'Email' ? '_blank' : undefined}
                rel={info.name !== 'Email' ? 'noopener noreferrer' : undefined}
              >
                {info.value}
              </FooterLink>
            ))}
            <FooterLink href="/files/avinash_kumar_resume.pdf" target="_blank" sx={{ mt: 1 }}>
              Download Resume
            </FooterLink>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3, borderColor: 'divider' }} />
        
        <CopyrightText variant="body2" align="center">
          © {currentYear} Avinash Kumar. All rights reserved.
          <FavoriteIcon sx={{ fontSize: '0.9rem', mx: 0.5, color: theme.palette.secondary.main }} />
          Built with React, Material UI, and LLM magic
        </CopyrightText>
      </Container>
    </FooterContainer>
  );
};

export default Footer; 